
// src/pages/Leads/LeadView.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../utils/api";
import {
  ArrowLeftIcon,
  PhoneIcon,
  CalendarIcon,
  PlusIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  HomeIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import CreateTaskModal from "../../components/Task/CreateTaskModal";

// ... (keep your ROOM_PRESETS constant as it was)
const ROOM_PRESETS = {
  "Living Room": {
    electrical: { "Main Lights": 2, "Fan Points": 1, "TV Points": 2, "5A Sockets": 6, "AC Point": 1 },
    water: {},
    plumbing: {}
  },
  "Kitchen": {
    electrical: { "Lights": 4, "Exhaust Fan": 1, "Chimney": 1, "Fridge": 1, "Appliance Points": 2, "5A Sockets": 5 },
    water: { "Sink Inlet": 1, "RO Inlet": 1, "RO Drain": 1 },
    plumbing: { "Sink Drain": 1 }
  },
  "Master Bedroom": {
    electrical: { "Lights": 2, "Fan": 1, "Bedside Sockets": 4, "AC Point": 1, "TV Point": 1 },
    water: {},
    plumbing: {}
  },
  "Children’s Bedroom": {
    electrical: { "Lights": 2, "Fan": 1, "Sockets": 4, "Study Point": 1 },
    water: {},
    plumbing: {}
  },
  "Common Bathroom": {
    electrical: { "Light": 1, "Exhaust Fan": 1, "Geyser": 1 },
    water: { "Shower": 1, "Wash Basin Tap": 1, "Bucket Tap": 1, "Flush": 1 },
    plumbing: { "Wash Basin Drain": 1, "Shower Drain": 1, "WC Drain": 1 }
  },
  "Attached Bathroom": {
    electrical: { "Ceiling Light": 1, "Mirror Light": 1, "Geyser": 1 },
    water: { "Shower": 1, "Tap": 1, "Flush": 1 },
    plumbing: { "Wash Basin Drain": 1, "WC Drain": 1, "Shower Drain": 1 }
  },
  "Utility Area": {
    electrical: { "Light": 1, "Washing Machine": 1 },
    water: { "Washing Machine Inlet": 1 },
    plumbing: { "Drain Outlet": 1 }
  },
  "Balcony": {
    electrical: { "Light": 1, "Socket": 1 },
    water: { "Tap": 1 },
    plumbing: {}
  }
};

export default function LeadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [lead, setLead] = useState(null);
  const [calls, setCalls] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [tab, setTab] = useState(0);
  const [showCreateTask, setShowCreateTask] = useState(false);

  const [form, setForm] = useState({
    status: "Connected",
    notes: "",
    next_followup: "",
  });

  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [savedRooms, setSavedRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState({
    room_type: "",
    walls: [
      { length_mm: "", height_mm: "" },
      { length_mm: "", height_mm: "" },
      { length_mm: "", height_mm: "" },
      { length_mm: "", height_mm: "" }
    ],
    points: { electrical: {}, water: {}, plumbing: {} },
    remarks: "",
    photo: null
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const action = params.get("action");
    if (tabParam) setTab(Number(tabParam));
    if (action === "createTask") setShowCreateTask(true);
  }, [location.search]);

  const addCall = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads/call", {
        lead_id: id,
        status: form.status,
        notes: form.notes,
        next_followup: form.next_followup,
      });
      setForm({ status: "Connected", notes: "", next_followup: "" });
      load();
    } catch (err) {
      alert("Failed to add call");
    }
  };

  async function load() {
    setLoading(true);
    try {
      const [leadRes, callsRes, followRes, pipelineRes] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/leads/${id}/calls`),
        api.get(`/leads/${id}/followups`),
        api.get(`/leads/${id}/pipeline`)
      ]);

      setLead(leadRes.data.lead || leadRes.data);
      setCalls(callsRes.data.calls || []);
      setFollowups(followRes.data.followups || []);
      setPipeline(pipelineRes.data);
      setSiteVisits(pipelineRes.data.visits || []);

      if (pipelineRes.data.measurement?.[0]) {
        const m = pipelineRes.data.measurement[0];
        let parsedRooms = [];
        let photoPaths = [];

        try {
          const raw = m.room_details || "[]";
          parsedRooms = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (!Array.isArray(parsedRooms)) parsedRooms = [];
        } catch (e) {
          console.error("Parse error room_details", e);
        }

        try {
          const rawPhotos = m.photo_paths || "[]";
          photoPaths = typeof rawPhotos === "string" ? JSON.parse(rawPhotos) : rawPhotos;
        } catch (e) {
          console.error("Parse error photo_paths", e);
        }

        const baseUrl = process.env.REACT_APP_API_URL || "https://api.onedesk.digital";

        let photoIndex = 0;
        const roomsWithPhotos = parsedRooms.map((room) => {
          const photos = [];
          if (photoIndex < photoPaths.length) {
            photos.push(`${baseUrl}/${photoPaths[photoIndex++]}`);
          }
          return {
            ...room,
            photos: photos || [],
            photo_url: room.photo_url || null
          };
        });

        setSavedRooms(roomsWithPhotos);
      } else {
        setSavedRooms([]);
      }
    } catch (err) {
      console.error("Failed to load lead:", err);
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  const calculateRoomArea = (walls) => {
    return walls.reduce((sum, w) => {
      const l = parseFloat(w.length_mm) || 0;
      const h = parseFloat(w.height_mm) || 0;
      return sum + (l * h / 92903.04);
    }, 0);
  };

  const totalSavedArea = savedRooms.reduce((s, r) => s + parseFloat(r.total_area_sqft || 0), 0).toFixed(1);
  const totalNewArea = rooms.reduce((s, r) => s + parseFloat(r.total_area_sqft || 0), 0).toFixed(1);
  const grandTotalArea = (parseFloat(totalSavedArea) + parseFloat(totalNewArea || 0)).toFixed(1);

  const handleRoomTypeChange = (type) => {
    setCurrentRoom(prev => ({
      ...prev,
      room_type: type,
      points: ROOM_PRESETS[type] || { electrical: {}, water: {}, plumbing: {} }
    }));
  };

  const addRoomToMeasurement = () => {
    if (!currentRoom.room_type) return alert("Please select a room type");
    const hasWall = currentRoom.walls.some(w => w.length_mm && w.height_mm);
    if (!hasWall) return alert("Enter at least one wall measurement");

    const total_area_sqft = calculateRoomArea(currentRoom.walls).toFixed(2);

    setRooms(prev => [...prev, {
      ...currentRoom,
      total_area_sqft,
      photo_url: currentRoom.photo ? URL.createObjectURL(currentRoom.photo) : null
    }]);

    setCurrentRoom({
      room_type: "",
      walls: [{ length_mm: "", height_mm: "" }, { length_mm: "", height_mm: "" }, { length_mm: "", height_mm: "" }, { length_mm: "", height_mm: "" }],
      points: { electrical: {}, water: {}, plumbing: {} },
      remarks: "",
      photo: null
    });
  };

  const removeRoomFromList = (i) => setRooms(rooms.filter((_, idx) => idx !== i));

  const saveFullMeasurement = async () => {
    if (!rooms.length) return alert("No rooms to save");
    setSaving(true);

    const fd = new FormData();
    fd.append("lead_id", id);
    fd.append("room_details", JSON.stringify(rooms.map(r => ({
      room_type: r.room_type,
      walls: r.walls,
      total_area_sqft: r.total_area_sqft,
      points: r.points,
      remarks: r.remarks || "",
      photo_path: r.photo ? `temp_${Date.now()}_${r.room_type}.jpg` : null
    }))));

    rooms.forEach(r => r.photo && fd.append("photos", r.photo));

    try {
      await api.post("/leads/measurement", fd);
      alert("Measurement saved successfully!");
      load();
      setRooms([]);
    } catch (err) {
      console.error(err);
      alert("Failed to save measurement");
    } finally {
      setSaving(false);
    }
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'call':
        return <PhoneIcon className="w-6 h-6 text-blue-600" />;
      case 'followup':
        return <ChatBubbleLeftIcon className="w-6 h-6 text-purple-600" />;
      case 'sitevisit':
        return <CalendarIcon className="w-6 h-6 text-amber-600" />;
      case 'note':
        return <DocumentTextIcon className="w-6 h-6 text-gray-600" />;
      case 'created':
        return <ClockIcon className="w-6 h-6 text-green-600" />;
      default:
        return <DocumentTextIcon className="w-6 h-6 text-gray-600" />;
    }
  };

  const getTimeline = () => {
    let allActivities = [
      ...calls.map(c => ({ type: 'call', date: new Date(c.created_at || c.call_time), note: `Call - Status: ${c.status}${c.notes ? `, Notes: ${c.notes}` : ''}` })),
      ...followups.map(f => ({ type: 'followup', date: new Date(f.scheduled_at), note: `Follow-up${f.notes ? `: ${f.notes}` : ''}` })),
      ...siteVisits.map(v => ({ type: 'sitevisit', date: new Date(v.scheduled_date), note: `Site Visit - Status: ${v.status || 'Scheduled'}${v.notes ? `, Notes: ${v.notes}` : ''}` })),
    ];
    if (lead.notes) {
      allActivities.push({ type: 'note', date: new Date(lead.created_at), note: `Note: ${lead.notes}` });
    }
    allActivities.push({ type: 'created', date: new Date(lead.created_at), note: 'Lead created' });

    return allActivities.sort((a, b) => b.date - a.date);
  };

  const renderRoomCard = (room, index, isSaved = false) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <HomeIcon className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg">
              {index + 1}. {room.room_type || "Unknown Room"}
            </h3>
            <p className="text-sm opacity-90 mt-1">
              {room.total_area_sqft || "0"} sqft
            </p>
          </div>
        </div>
        {!isSaved && (
          <button
            onClick={() => removeRoomFromList(index)}
            className="p-2 hover:bg-white/20 rounded-full transition"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="p-6 space-y-6">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Wall Measurements</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase">Wall</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase">Length (mm)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase">Height (mm)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase">Area (sqft)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {(room.walls || []).filter(w => w.length_mm && w.height_mm).map((w, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-sm">Wall {i + 1}</td>
                    <td className="px-4 py-2 text-sm">{w.length_mm}</td>
                    <td className="px-4 py-2 text-sm">{w.height_mm}</td>
                    <td className="px-4 py-2 text-sm font-medium">
                      {((w.length_mm * w.height_mm) / 92903.04).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {(room.walls || []).every(w => !w.length_mm || !w.height_mm) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                      No wall measurements
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {room.points && typeof room.points === "object" && Object.keys(room.points).length > 0 && (
          <>
            {Object.entries(room.points).map(([cat, items]) => {
              if (!items || typeof items !== "object" || Object.keys(items).length === 0) return null;
              return (
                <div key={cat} className="mt-6">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 capitalize">
                    {cat === "electrical" ? "Electrical" : cat === "water" ? "Water Supply" : "Plumbing"} Points
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(items).map(([name, qty]) => (
                      <div key={name} className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-sm font-medium">
                        {name}: <span className="font-bold">{qty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {room.remarks && (
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
            <p className="text-sm font-medium flex items-start gap-2">
              <DocumentTextIcon className="w-5 h-5 text-gray-500 mt-0.5" />
              <span className="text-gray-700 dark:text-gray-300">{room.remarks}</span>
            </p>
          </div>
        )}

        {(room.photos && room.photos.length > 0) || room.photo_url ? (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Photos</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {(room.photos || []).map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Photo ${i + 1}`}
                  className="w-full h-40 object-cover rounded-lg shadow-sm hover:shadow-md transition"
                />
              ))}
              {room.photo_url && (!room.photos || room.photos.length === 0) && (
                <img
                  src={room.photo_url}
                  alt="Preview"
                  className="w-full h-40 object-cover rounded-lg shadow-sm hover:shadow-md transition"
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
        <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-orange-500 border-opacity-70"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
        <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-5">Lead Not Found</h2>
          <button
            onClick={() => navigate("/leads")}
            className="px-8 py-3.5 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl shadow-lg transition"
          >
            Back to Leads
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-10">

        {/* ──── HEADER ──────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/70 dark:border-gray-700/50 p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex items-start gap-5">
              <button
                onClick={() => navigate("/leads")}
                className="p-3.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeftIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </button>

              <div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                  {lead.lead_uid || lead.id} — {lead.name || "Unnamed Lead"}
                </h1>

                <div className="flex flex-wrap gap-4 mt-4">
                  {lead.mobile && (
                    <div className="flex items-center gap-2.5 px-4 py-2 bg-orange-50 dark:bg-orange-950/40 rounded-full border border-orange-100 dark:border-orange-900/30">
                      <PhoneIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      <span className="font-medium text-gray-800 dark:text-gray-200">{lead.mobile}</span>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-2.5 px-4 py-2 bg-orange-50 dark:bg-orange-950/40 rounded-full border border-orange-100 dark:border-orange-900/30">
                      <EnvelopeIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      <span className="font-medium text-gray-800 dark:text-gray-200">{lead.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-full">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Stage:</span>
                    <span className="px-4 py-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full text-sm shadow-sm">
                      {lead.pipeline_stage || "Lead"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-6 lg:mt-0">
              <button
                onClick={() => navigate(`/leads/${id}/site-visit`)}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <CalendarIcon className="w-5 h-5" />
                Schedule Site Visit
              </button>

              {/* <button
                onClick={() => setShowCreateTask(true)}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <PlusIcon className="w-5 h-5" />
                Create Task
              </button> */}
            </div>
          </div>
        </div>

        {/* ──── TABS ────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/70 dark:border-gray-700/50 overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-gray-800">
            {["Overview", "Calls & Files", "Follow-ups & Visits"].map((label, i) => (
              <button
                key={label}
                onClick={() => setTab(i)}
                className={`
                  relative flex-1 py-5 px-8 text-base md:text-lg font-medium transition-all duration-200
                  ${tab === i
                    ? "text-orange-600 dark:text-orange-400 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-gradient-to-r after:from-orange-500 after:to-orange-400"
                    : "text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50/30 dark:hover:bg-orange-950/20"
                  }
                `}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="p-6 md:p-8">
            {/* ──── Tab: Overview ───────────────────────────────────── */}
            {/* {tab === 0 && (
              <div className="space-y-8">
                <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 p-7 rounded-2xl border border-gray-200/60 dark:border-gray-700/40 shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                    <ClockIcon className="w-7 h-7 text-orange-500" />
                    Activity Timeline
                  </h3>
                 <div className="space-y-4">
                    {getTimeline().map((act, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/40">
                          {getIconForType(act.type)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {act.date.toLocaleString()}
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 mt-1">
                            {act.note}
                          </p>
                        </div>
                      </div>
                    ))}
                    {getTimeline().length === 0 && (
                      <p className="text-center text-gray-500 dark:text-gray-400">No activities yet</p>
                    )}
                  </div>
                </div>
              </div>
            )} */}

            {/* ──── Tab: Calls & Files ──────────────────────────────── */}
            {tab === 1 && (
              <div className="space-y-10">
                {/* Log New Call Card */}
                <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/40 p-7 md:p-9">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-7 flex items-center gap-3">
                    <PhoneIcon className="w-7 h-7 text-orange-500" />
                    Log New Call
                  </h3>

                  <form onSubmit={addCall} className="space-y-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2.5 tracking-wide">
                          Call Status
                        </label>
                        <select
                          value={form.status}
                          onChange={(e) => setForm({ ...form, status: e.target.value })}
                          className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition shadow-sm"
                        >
                          {["Connected", "RNR", "Busy", "Wrong Number", "Not Interested", "Follow-up Required"].map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2.5 tracking-wide">
                          Next Follow-up
                        </label>
                        <input
                          type="datetime-local"
                          value={form.next_followup}
                          onChange={(e) => setForm({ ...form, next_followup: e.target.value })}
                          className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition shadow-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2.5 tracking-wide">
                        Notes
                      </label>
                      <textarea
                        rows={5}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition shadow-sm resize-none"
                        placeholder="Call summary, objections, next steps..."
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                    >
                      Save Call Log
                    </button>
                  </form>
                </div>

                </div>
            )}
              

            {/* ──── Tab: Follow-ups & Visits ────────────────────────── */}
            {tab === 2 && (
              <div className="space-y-8">
                <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/40 p-7 md:p-9">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-7 flex items-center gap-3">
                    <CalendarIcon className="w-7 h-7 text-orange-500" />
                    Upcoming Activities
                  </h3>

              {(followups.length === 0 && siteVisits.length === 0) ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                      No activities scheduled
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {followups.map(f => (
                        <div key={f.id} className="p-5 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-700">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/40">
                              <PhoneIcon className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Call Follow-up</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {new Date(f.scheduled_at).toLocaleString()}
                              </p>
                              {f.notes && <p className="mt-2 text-gray-700 dark:text-gray-300">{f.notes}</p>}
                            </div>
                          </div>
                        </div>
                      ))}

                      {siteVisits.map(visit => (
                        <div key={visit.id} className="p-5 bg-amber-50 dark:bg-amber-900/30 rounded-xl border border-amber-200 dark:border-amber-800">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-full bg-amber-100 dark:bg-amber-800/40">
                              <CalendarIcon className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Site Visit</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {new Date(visit.scheduled_date).toLocaleString()}
                              </p>
                              <p className="mt-1">
                                Status: <span className="font-medium">{visit.status || "Scheduled"}</span>
                              </p>
                              {visit.notes && <p className="mt-2 text-gray-700 dark:text-gray-300">{visit.notes}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Task Modal */}
        <CreateTaskModal
          open={showCreateTask}
          onClose={(success) => {
            setShowCreateTask(false);
            if (success) {
              // alert("Task created successfully!");
              load();
            }
          }}
          defaultModule="Lead"
          defaultRecord={{
            id: lead?.id,
            uid: lead?.lead_uid || `LW-${String(lead?.id || "").padStart(6, "0")}`,
            name: lead?.name,
          }}
        />
      </div>
    </div>
  );
}